/**
 * Reportes → Ventas de GNV (`/reportes/ventas-gnv`).
 *
 * Puerto de `Reportes::reporte_gnv` + `get_reporte_gnv_data` /
 * `get_reporte_gnv_stats` + `Liquidaciones_model::get_reporte_gnv` y
 * `get_stats_gnv` + `views/menu/reportes/reporte_gnv.php`.
 *
 * La pantalla del panel anterior pedía el listado y las estadísticas en dos
 * llamadas (con el rango por defecto de los últimos 30 días) y dibujaba seis
 * tarjetas de resumen sobre una tabla con búsqueda, paginación y exportaciones.
 *
 * Reglas conservadas:
 * - Se listan los **turnos con venta de GNV registrada** (`turno.tiene_gnv`), del
 *   rango de fechas elegido, con su volumen en m³, su equivalente en galones
 *   (m³ ÷ 3,1) y el precio por m³.
 * - El rango por defecto es el de los últimos 30 días.
 *
 * Diferencias anotadas: el resumen se calcula con las mismas filas que se listan
 * (así siempre cuadra con la tabla) y la exportación es CSV (que abre Excel) más
 * el PDF, como el resto de reportes del panel.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import { reportesApi, useVentasGnv, type FiltrosVentasGnv } from '../reportes.api';

/** Fecha local `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: los últimos 30 días, como el panel anterior. */
function rangoPorDefecto(): FiltrosVentasGnv {
  const hasta = new Date();
  const desde = new Date();

  desde.setDate(desde.getDate() - 30);

  return { desde: fechaLocal(desde), hasta: fechaLocal(hasta) };
}

/** Volumen con dos decimales y el sufijo de la unidad. */
const m3 = new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ReporteVentasGnvPage() {
  useDocumentTitle('Reportes · Ventas de GNV');

  const [filtros, setFiltros] = useState<FiltrosVentasGnv>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const consulta = useVentasGnv(filtros);
  const filas = consulta.data?.filas ?? [];
  // El listado se pagina en el navegador; los contadores y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const resumen = consulta.data?.resumen;

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarVentasGnv(filtros), {
        nombre: `ventas-gnv-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const abrirPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfVentasGnv(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const tarjetas = [
    {
      etiqueta: 'Turnos con GNV',
      valor: formatNumber(resumen?.total_turnos ?? 0),
      detalle: `${formatNumber(resumen?.dias_activos ?? 0)} día(s) · ${formatNumber(
        resumen?.trabajadores ?? 0,
      )} trabajador(es)`,
    },
    {
      etiqueta: 'Volumen',
      valor: `${m3.format(resumen?.total_volumen_m3 ?? 0)} m³`,
      detalle: `${formatNumber(resumen?.total_galones ?? 0)} gal equivalente`,
    },
    {
      etiqueta: 'Total vendido',
      valor: formatCurrency(resumen?.total_monto ?? 0),
      detalle: `Precio medio ${formatCurrency(resumen?.precio_m3 ?? 0)} / m³`,
    },
    {
      etiqueta: 'Promedio por turno',
      valor: formatCurrency(
        (resumen?.total_turnos ?? 0) > 0 ? (resumen?.total_monto ?? 0) / resumen!.total_turnos : 0,
      ),
      detalle: `${m3.format(
        (resumen?.total_turnos ?? 0) > 0 ? (resumen?.total_volumen_m3 ?? 0) / resumen!.total_turnos : 0,
      )} m³ por turno`,
    },
  ];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Ventas de GNV</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los turnos con venta de GNV del periodo, con su volumen, su equivalente en galones y su
            importe.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={abrirPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="grid items-end gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Desde</span>
            <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
              <CampoFecha
                tipo="date"
                value={filtros.desde}
                onChange={(evento) =>
                  setFiltros((actual) => ({ ...actual, desde: evento.target.value || actual.desde }))
                }
                className="w-full px-3 text-sm"
              />
            </span>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Hasta</span>
            <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
              <CampoFecha
                tipo="date"
                value={filtros.hasta}
                onChange={(evento) =>
                  setFiltros((actual) => ({ ...actual, hasta: evento.target.value || actual.hasta }))
                }
                className="w-full px-3 text-sm"
              />
            </span>
          </label>

          <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
            <Button
              variant="outline"
              onClick={() => setFiltros(rangoPorDefecto())}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
            >
              Últimos 30 días
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const mes = new Date().getMonth() + 1;
                const anio = new Date().getFullYear();
                const primero = `${anio}-${String(mes).padStart(2, '0')}-01`;

                setFiltros({ desde: primero, hasta: fechaLocal(new Date()) });
              }}
            >
              Este mes
            </Button>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron cargar las ventas de GNV">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="warning" title="Revisa el dato">
          {aviso}
        </Alert>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!consulta.isLoading ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {tarjetas.map((tarjeta) => (
              <Card key={tarjeta.etiqueta}>
                <CardContent className="p-4">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    {tarjeta.etiqueta}
                  </p>
                  <p className="mt-1 text-xl font-bold text-ink-900">{tarjeta.valor}</p>
                  <p className="mt-0.5 text-xs text-ink-500">{tarjeta.detalle}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {filas.length === 0 ? (
            <Alert variant="info" title="Sin ventas de GNV en el periodo">
              El GNV se registra desde el turno (botón *Bóvedas · Gastos* → pestaña *GNV*). Prueba
              con otro rango de fechas.
            </Alert>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
                  <p className="text-sm font-semibold text-ink-800">
                    Registro de ventas GNV por turno
                  </p>
                  <Badge variant="brand">{formatNumber(filas.length)} fila(s)</Badge>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="bg-ink-50">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-3 py-2">Turno</th>
                        <th className="px-3 py-2">Fecha</th>
                        <th className="px-3 py-2">Trabajador</th>
                        <th className="px-3 py-2">Inicio</th>
                        <th className="px-3 py-2">Fin</th>
                        <th className="px-3 py-2 text-right">Volumen (m³)</th>
                        <th className="px-3 py-2 text-right">Galones equiv.</th>
                        <th className="px-3 py-2 text-right">Monto</th>
                        <th className="px-3 py-2 text-right">Precio / m³</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginacion.visibles.map((fila) => (
                        <tr key={fila.turno_id} className="border-t border-ink-100">
                          <td className="px-3 py-2 font-semibold text-ink-900">#{fila.turno_id}</td>
                          <td className="px-3 py-2 text-ink-600">{formatDateValue(fila.fecha)}</td>
                          <td className="px-3 py-2 text-ink-700">{fila.trabajador}</td>
                          <td className="px-3 py-2 text-ink-600">{fila.hora_inicio}</td>
                          <td className="px-3 py-2 text-ink-600">{fila.hora_fin}</td>
                          <td className="px-3 py-2 text-right text-ink-800">
                            {m3.format(fila.volumen_m3)}
                          </td>
                          <td className="px-3 py-2 text-right text-ink-600">
                            {formatNumber(fila.galones)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-ink-900">
                            {formatCurrency(fila.monto)}
                          </td>
                          <td className="px-3 py-2 text-right text-ink-600">
                            {formatCurrency(fila.precio_m3)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-ink-50 font-semibold">
                      <tr className="border-t-2 border-ink-200">
                        <td className="px-3 py-2" colSpan={3}>
                          TOTALES
                        </td>
                        <td className="px-3 py-2 text-xs text-ink-500" colSpan={2}>
                          {formatNumber(resumen?.dias_activos ?? 0)} día(s) ·{' '}
                          {formatNumber(resumen?.trabajadores ?? 0)} trabajador(es)
                        </td>
                        <td className="px-3 py-2 text-right">
                          {m3.format(resumen?.total_volumen_m3 ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatNumber(resumen?.total_galones ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(resumen?.total_monto ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(resumen?.precio_m3 ?? 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <Paginador {...paginacion} etiqueta="turnos" />
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El GNV se guarda en el turno y no entra en el cuadre de caja de la liquidación: este reporte
        es el que lleva su volumen y su importe. La equivalencia en galones es volumen ÷ 3,1, como
        en el panel anterior.
      </p>
    </div>
  );
}
